// TMP editor — cover & specifics, sections (narrative/questions/image), risk assessment, versions, exports.
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    ArrowLeft, Save, Loader2, Plus, Trash2, Cone, FileText, FileType2, History,
    Upload, Image as ImageIcon, X, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError, filenameFromResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import Pill from "@/components/Pill";
import { useOfflinePhotoQueue } from "@/hooks/useOfflinePhotoQueue";
import { PendingPhotoThumbs } from "@/components/PendingPhotoThumbs";

const CAN_EDIT = new Set(["director", "administrator", "consultant"]);
const STATUSES = ["Draft", "Under Review", "Approved", "Issued", "Archived"];
const STATUS_TONE = { Draft: "neutral", "Under Review": "amber", Approved: "green", Issued: "blue", Archived: "neutral" };
const ANSWERS = ["", "Yes", "No", "N/A"];
const RATINGS = ["", "H", "M", "L"];
const inputCls = "w-full border rounded-lg px-3 py-2 text-sm mt-0.5";

export default function TmpEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const backUrl = params.get("back");
    const { user } = useAuth();
    const { isInternalView } = useView();
    const canEdit = CAN_EDIT.has(user?.role) && isInternalView;

    const [rec, setRec] = useState(null);
    const [tab, setTab] = useState("cover");
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [exporting, setExporting] = useState("");

    const load = useCallback(async () => {
        try { const { data } = await api.get(`/tmp/${id}`); setRec(data); }
        catch (e) { toast.error("Failed to load TMP"); navigate("/app/tmp"); }
    }, [id, navigate]);
    useEffect(() => { load(); }, [load]);

    const patch = (changes) => { setRec((r) => ({ ...r, ...changes })); setDirty(true); };

    const save = useCallback(async () => {
        if (!rec) return;
        setSaving(true);
        try {
            const body = {
                project_id: rec.project_id || null, building_id: rec.building_id || null,
                status: rec.status, project_name: rec.project_name, project_number: rec.project_number,
                original_issue_date: rec.original_issue_date, revised_date: rec.revised_date,
                nominated_banksman: rec.nominated_banksman, banksman_competency: rec.banksman_competency,
                communication_method: rec.communication_method, headline_specifics: rec.headline_specifics,
                sections: rec.sections, risk_rows: rec.risk_rows, structure: rec.structure,
            };
            const { data } = await api.patch(`/tmp/${id}`, body);
            setRec(data); setDirty(false);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Save failed"); }
        finally { setSaving(false); }
    }, [rec, id]);

    const doExport = async (kind) => {
        if (dirty && canEdit) await save();
        setExporting(kind);
        try {
            const res = await api.get(`/tmp/${id}/${kind}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = filenameFromResponse(res, `TMP-${rec.reference}-v${rec.version}.${kind === "docx" ? "docx" : "pdf"}`); a.click();
            URL.revokeObjectURL(url);
            toast.success(`${kind.toUpperCase()} downloaded`);
        } catch (e) { toast.error(`${kind.toUpperCase()} export failed`); }
        finally { setExporting(""); }
    };

    if (!rec) return <div className="grid place-items-center py-32"><Loader2 className="w-7 h-7 animate-spin text-[#C75A1B]" /></div>;

    const tabs = [
        { id: "cover", label: "Cover & Specifics" },
        { id: "sections", label: "Sections" },
        { id: "risk", label: "Risk Assessment" },
        { id: "versions", label: "Versions" },
    ];

    return (
        <div className="p-6 max-w-[1180px] mx-auto" data-testid="tmp-editor">
            <button onClick={() => navigate(backUrl || "/app/tmp")} className="inline-flex items-center gap-1.5 text-sm text-[#6F6A64] hover:text-[#1F1F1F] mb-3">
                <ArrowLeft className="w-4 h-4" /> {backUrl ? "Back to project" : "All TMPs"}
            </button>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2.5">
                        <h1 className="text-2xl font-serif text-[#1F1F1F]">{rec.project_name || "Untitled TMP"}</h1>
                        <Pill tone={STATUS_TONE[rec.status]}>{rec.status}</Pill>
                    </div>
                    <p className="text-sm text-[#6F6A64] mt-1">{rec.reference} · v{rec.version} · {rec.project_number || "—"}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {canEdit && (
                        <select data-testid="tmp-status-select" value={rec.status} onChange={(e) => patch({ status: e.target.value })}
                            className="border rounded-lg px-2.5 py-2 text-sm" style={{ borderColor: "#E5E0DA" }}>
                            {STATUSES.map((s) => <option key={s}>{s}</option>)}
                        </select>
                    )}
                    <button onClick={() => doExport("docx")} disabled={!!exporting} data-testid="tmp-export-docx"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border disabled:opacity-60" style={{ borderColor: "#E5E0DA", color: "#1F1F1F" }}>
                        {exporting === "docx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileType2 className="w-4 h-4" />} Word
                    </button>
                    <button onClick={() => doExport("pdf")} disabled={!!exporting} data-testid="tmp-export-pdf"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border disabled:opacity-60" style={{ borderColor: "#E5E0DA", color: "#1F1F1F" }}>
                        {exporting === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} PDF
                    </button>
                    {canEdit && (
                        <button onClick={save} disabled={saving || !dirty} data-testid="tmp-save-button"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ background: "#C75A1B" }}>
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {dirty ? "Save" : "Saved"}
                        </button>
                    )}
                </div>
            </div>

            <div className="flex gap-1 mt-5 border-b overflow-x-auto" style={{ borderColor: "#E5E0DA" }}>
                {tabs.map((t) => (
                    <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tmp-tab-${t.id}`}
                        className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.id ? "" : "border-transparent text-[#6F6A64] hover:text-[#1F1F1F]"}`}
                        style={tab === t.id ? { borderColor: "#C75A1B", color: "#C75A1B" } : {}}>{t.label}</button>
                ))}
            </div>

            <div className="mt-5">
                {tab === "cover" && <CoverTab rec={rec} patch={patch} canEdit={canEdit} />}
                {tab === "sections" && <SectionsTab rec={rec} patch={patch} canEdit={canEdit} tmpId={id} reload={load} setTab={setTab} />}
                {tab === "risk" && <RiskTab rec={rec} patch={patch} canEdit={canEdit} />}
                {tab === "versions" && <VersionsTab rec={rec} reload={load} canEdit={canEdit} />}
            </div>
        </div>
    );
}

const F = ({ label, children }) => (<div><label className="text-xs font-semibold text-[#6F6A64]">{label}</label>{children}</div>);

function CoverTab({ rec, patch, canEdit }) {
    const [projects, setProjects] = useState([]);
    const [buildings, setBuildings] = useState([]);
    useEffect(() => {
        if (!rec.client_id) return;
        api.get(`/projects?client_id=${rec.client_id}`).then(({ data }) => setProjects(data || [])).catch(() => {});
        api.get(`/buildings?client_id=${rec.client_id}`).then(({ data }) => setBuildings(data || [])).catch(() => {});
    }, [rec.client_id]);
    const txt = (key, label, type = "text") => (
        <F label={label}>
            <input type={type} className={inputCls} style={{ borderColor: "#E5E0DA" }} disabled={!canEdit}
                data-testid={`tmp-field-${key}`} value={rec[key] || ""} onChange={(e) => patch({ [key]: e.target.value })} />
        </F>
    );
    const specifics = rec.headline_specifics || [];
    const setSpecific = (i, v) => patch({ headline_specifics: specifics.map((s, si) => si === i ? v : s) });
    return (
        <div className="space-y-5">
            <div className="bg-white border rounded-2xl p-5" style={{ borderColor: "#E5E0DA" }} data-testid="tmp-link-fields">
                <h3 className="font-semibold mb-3">Linked project</h3>
                <div className="grid sm:grid-cols-3 gap-3">
                    <F label="Project">
                        <select className={inputCls} style={{ borderColor: "#E5E0DA" }} disabled={!canEdit}
                            data-testid="tmp-assign-project" value={rec.project_id || ""}
                            onChange={(e) => patch({ project_id: e.target.value || null })}>
                            <option value="">— Not assigned —</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.reference} · {p.name}</option>)}
                        </select>
                    </F>
                    <F label="Structure / building">
                        <select className={inputCls} style={{ borderColor: "#E5E0DA" }} disabled={!canEdit}
                            data-testid="tmp-assign-building" value={rec.building_id || ""}
                            onChange={(e) => patch({ building_id: e.target.value || null })}>
                            <option value="">— None —</option>
                            {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </F>
                </div>
            </div>
            <div className="bg-white border rounded-2xl p-5" style={{ borderColor: "#E5E0DA" }} data-testid="tmp-cover-fields">
                <h3 className="font-semibold mb-3">Project header</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {txt("project_name", "Project name")}
                    {txt("project_number", "Project number")}
                    {txt("structure", "Structure / location")}
                    {txt("original_issue_date", "Original issue date", "date")}
                    {txt("revised_date", "Revised date", "date")}
                    {txt("nominated_banksman", "Nominated banksman")}
                    {txt("banksman_competency", "Banksman competency")}
                    {txt("communication_method", "Communication method")}
                </div>
            </div>
            <div className="bg-white border rounded-2xl p-5" style={{ borderColor: "#E5E0DA" }} data-testid="tmp-specifics">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">Headline Site Specifics</h3>
                    {canEdit && <button data-testid="tmp-add-specific" onClick={() => patch({ headline_specifics: [...specifics, ""] })}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg" style={{ background: "#FBEFE5", color: "#C75A1B" }}>
                        <Plus className="w-4 h-4" /> Add bullet</button>}
                </div>
                <div className="space-y-2">
                    {specifics.map((s, i) => (
                        <div key={i} className="flex items-start gap-2" data-testid={`tmp-specific-${i}`}>
                            <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#C75A1B] shrink-0" />
                            <textarea rows={1} disabled={!canEdit} className="flex-1 border rounded-lg px-2.5 py-1.5 text-sm resize-y" style={{ borderColor: "#E5E0DA" }}
                                value={s} onChange={(e) => setSpecific(i, e.target.value)} />
                            {canEdit && <button onClick={() => patch({ headline_specifics: specifics.filter((_, si) => si !== i) })}
                                className="text-[#B33A2E] mt-1.5"><Trash2 className="w-4 h-4" /></button>}
                        </div>
                    ))}
                    {specifics.length === 0 && <p className="text-sm text-[#6F6A64]">No headline specifics yet.</p>}
                </div>
            </div>
        </div>
    );
}

function SectionsTab({ rec, patch, canEdit, tmpId, reload, setTab }) {
    const sections = rec.sections || [];
    const updateSection = (sid, p) => patch({ sections: sections.map((s) => s.id === sid ? { ...s, ...p } : s) });
    return (
        <div className="space-y-4" data-testid="tmp-sections">
            {sections.map((s) => (
                <div key={s.id} className="bg-white border rounded-2xl p-5" style={{ borderColor: "#E5E0DA" }} data-testid={`tmp-section-${s.key}`}>
                    <h3 className="font-semibold text-[#1F1F1F]">{s.number ? `${s.number}. ` : ""}{s.title}</h3>
                    {s.kind === "narrative" && (
                        <textarea rows={4} disabled={!canEdit} data-testid={`tmp-narrative-${s.key}`}
                            className="w-full border rounded-lg px-3 py-2 text-sm mt-2 resize-y" style={{ borderColor: "#E5E0DA" }}
                            value={s.body || ""} onChange={(e) => updateSection(s.id, { body: e.target.value })} />
                    )}
                    {s.kind === "questions" && (
                        <QuestionsEditor section={s} canEdit={canEdit} onChange={(qs) => updateSection(s.id, { questions: qs })} />
                    )}
                    {s.kind === "risk" && (
                        <div className="mt-2 text-sm text-[#6F6A64] flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-[#C68A1C]" />
                            {(rec.risk_rows || []).length} hazards captured — edit in the
                            <button onClick={() => setTab("risk")} className="font-semibold underline" style={{ color: "#C75A1B" }}>Risk Assessment</button> tab.
                        </div>
                    )}
                    {s.kind === "image" && (
                        <ImageSection section={s} rec={rec} canEdit={canEdit} tmpId={tmpId} reload={reload} updateSection={updateSection} />
                    )}
                </div>
            ))}
        </div>
    );
}

function QuestionsEditor({ section, canEdit, onChange }) {
    const qs = section.questions || [];
    const update = (i, p) => onChange(qs.map((q, qi) => qi === i ? { ...q, ...p } : q));
    return (
        <div className="mt-3 space-y-3">
            {section.body && <p className="text-sm text-[#6F6A64]">{section.body}</p>}
            {qs.map((q, i) => (
                <div key={q.id || i} className="border rounded-xl p-3" style={{ borderColor: "#EDE9E3" }} data-testid={`tmp-q-${section.key}-${i}`}>
                    <div className="flex items-start gap-2">
                        <textarea rows={1} disabled={!canEdit} className="flex-1 text-sm font-medium border-0 bg-transparent resize-y focus:ring-0 p-0 text-[#1F1F1F]"
                            value={q.text || ""} onChange={(e) => update(i, { text: e.target.value })} />
                        <select disabled={!canEdit} data-testid={`tmp-q-answer-${section.key}-${i}`} value={q.answer || ""} onChange={(e) => update(i, { answer: e.target.value || null })}
                            className="border rounded-lg px-2 py-1 text-sm shrink-0" style={{ borderColor: "#E5E0DA" }}>
                            {ANSWERS.map((a) => <option key={a} value={a}>{a || "—"}</option>)}
                        </select>
                        {canEdit && <button onClick={() => onChange(qs.filter((_, qi) => qi !== i))} className="text-[#B33A2E] shrink-0 mt-1"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2 mt-2">
                        <textarea rows={2} disabled={!canEdit} placeholder="Details / comments" className="border rounded-lg px-2.5 py-1.5 text-sm resize-y" style={{ borderColor: "#E5E0DA" }}
                            value={q.details || ""} onChange={(e) => update(i, { details: e.target.value })} />
                        <textarea rows={2} disabled={!canEdit} placeholder="Control measures" className="border rounded-lg px-2.5 py-1.5 text-sm resize-y" style={{ borderColor: "#E5E0DA" }}
                            value={q.controls || ""} onChange={(e) => update(i, { controls: e.target.value })} />
                    </div>
                </div>
            ))}
            {canEdit && <button data-testid={`tmp-add-q-${section.key}`} onClick={() => onChange([...qs, { id: crypto.randomUUID(), text: "New question", answer: null, details: "", controls: "" }])}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg" style={{ background: "#FBEFE5", color: "#C75A1B" }}>
                <Plus className="w-4 h-4" /> Add question</button>}
        </div>
    );
}

function ImageSection({ section, rec, canEdit, tmpId, reload, updateSection }) {
    const editable = canEdit && rec.status !== "Issued";
    const imgs = (rec.images || []).filter((im) => im.section_key === section.key).sort((a, b) => a.sort_order - b.sort_order);
    const { pendingPhotos, queuePhoto: queuePhotoOnly, retry: retryQueuedPhoto, discard: discardQueuedPhoto, discardAll: discardAllPendingPhotos } = useOfflinePhotoQueue({
        entityType: "tmp", entityId: tmpId, enabled: !!tmpId,
        recordLabel: `TMP — ${rec.project_name || rec.reference || "Untitled"} / ${section.title || section.key}`,
        onUploaded: reload,
    });
    const allPending = pendingPhotos.filter((p) => p.section_key === section.key);
    const upload = async (fileList) => {
        const files = Array.from(fileList || []);
        let queuedCount = 0;
        for (const file of files) {
            try { await queuePhotoOnly(file, { section_key: section.key, caption: section.caption || "" }); queuedCount += 1; }
            catch (e) { toast.error(`Could not save ${file.name} on this device`); }
        }
        if (!queuedCount) return;
        if (navigator.onLine) toast.success(queuedCount > 1 ? `${queuedCount} images added — uploading…` : "Image added — uploading…");
        else toast.info(queuedCount > 1 ? `${queuedCount} images saved on this device — will upload automatically` : "Image saved on this device — will upload automatically");
    };
    const removeImg = async (imgId) => {
        try { await api.delete(`/tmp/${tmpId}/images/${imgId}`); await reload(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed"); }
    };
    return (
        <div className="mt-3">
            <F label="Caption">
                <input disabled={!canEdit} className={inputCls} style={{ borderColor: "#E5E0DA" }} data-testid={`tmp-caption-${section.key}`}
                    value={section.caption || ""} onChange={(e) => updateSection(section.id, { caption: e.target.value })} />
            </F>
            <div className="flex flex-wrap gap-3 mt-3">
                {imgs.map((im) => (
                    <div key={im.id} className="relative border rounded-xl overflow-hidden" style={{ borderColor: "#E5E0DA", width: 220 }} data-testid={`tmp-image-${im.id}`}>
                        <img src={`${API}/tmp/${tmpId}/images/${im.id}/file`} alt={im.caption || section.title} className="w-full h-36 object-cover" />
                        {im.caption && <div className="text-[11px] text-[#6F6A64] p-2 truncate">{im.caption}</div>}
                        {editable && <button onClick={() => removeImg(im.id)} data-testid={`tmp-image-remove-${im.id}`}
                            className="absolute top-1.5 right-1.5 bg-white/90 rounded-full p-1 text-[#B33A2E]"><X className="w-3.5 h-3.5" /></button>}
                    </div>
                ))}
                {editable && (
                    <label className="border border-dashed rounded-xl grid place-items-center cursor-pointer hover:bg-[#FBFAF8]" style={{ borderColor: "#D8D2C8", width: 220, height: 144 }} data-testid={`tmp-upload-${section.key}`}>
                        <span className="text-center text-[#6F6A64]"><Upload className="w-6 h-6 mx-auto" /><span className="text-xs block mt-1">Upload image(s)</span></span>
                        <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
                    </label>
                )}
                {imgs.length === 0 && !editable && <p className="text-sm text-[#6F6A64] flex items-center gap-1.5"><ImageIcon className="w-4 h-4" /> No image uploaded.</p>}
            </div>
            {allPending.length > 0 && (
                <div data-testid={`tmp-photo-sync-status-${section.key}`} className="flex items-center gap-2 rounded-lg px-2.5 py-2 border mt-2" style={{ background: "#FBEFE3", borderColor: "#F0DCC2" }}>
                    <span className="text-[12px] text-[#9F4212] font-semibold flex-1">
                        {allPending.length} image{allPending.length !== 1 ? "s" : ""} {allPending.every((p) => p.failed) ? "failed to upload — tap below to retry or discard." : "saved on this device — uploading…"}
                    </span>
                    {editable && (
                        <button type="button" data-testid={`tmp-discard-all-pending-${section.key}`}
                            onClick={() => { if (window.confirm(`Discard all ${allPending.length} pending image(s)? They will not be uploaded.`)) discardAllPendingPhotos(); }}
                            className="text-[11px] font-semibold text-[#9F4212] hover:underline shrink-0">Discard all</button>
                    )}
                </div>
            )}
            <PendingPhotoThumbs pending={allPending} testId={`tmp-pending-images-${section.key}`} onRetry={retryQueuedPhoto} onDiscard={editable ? discardQueuedPhoto : null} />
        </div>
    );
}

function RiskTab({ rec, patch, canEdit }) {
    const rows = rec.risk_rows || [];
    const update = (i, p) => patch({ risk_rows: rows.map((r, ri) => ri === i ? { ...r, ...p } : r) });
    const addRow = () => patch({ risk_rows: [...rows, { id: crypto.randomUUID(), hazard: "", persons: "", consequences: "", risk_rating: "", controls: "", residual: "" }] });
    const cell = "w-full border rounded px-2 py-1 text-[13px] resize-y";
    return (
        <div className="bg-white border rounded-2xl p-5" style={{ borderColor: "#E5E0DA" }} data-testid="tmp-risk-table">
            <div className="flex items-center justify-between mb-3">
                <div><h3 className="font-semibold">Traffic Management Risk Assessment</h3>
                    <p className="text-xs text-[#6F6A64] mt-0.5">Risk rating: H = High, M = Medium, L = Low.</p></div>
                {canEdit && <button data-testid="tmp-add-risk" onClick={addRow} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg" style={{ background: "#FBEFE5", color: "#C75A1B" }}>
                    <Plus className="w-4 h-4" /> Add hazard</button>}
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-[13px]" style={{ minWidth: 900 }}>
                    <thead><tr style={{ background: "#C75A1B" }} className="text-white">
                        {["Hazard", "Persons in danger", "Harmful consequences", "Risk", "Control measures", "Residual", ""].map((h, i) => (
                            <th key={i} className="text-left font-semibold px-2 py-1.5">{h}</th>))}
                    </tr></thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={r.id || i} className="border-b align-top" style={{ borderColor: "#EDE9E3" }} data-testid={`tmp-risk-row-${i}`}>
                                <td className="p-1"><textarea rows={2} disabled={!canEdit} className={cell} style={{ borderColor: "#E5E0DA" }} value={r.hazard || ""} onChange={(e) => update(i, { hazard: e.target.value })} /></td>
                                <td className="p-1"><textarea rows={2} disabled={!canEdit} className={cell} style={{ borderColor: "#E5E0DA" }} value={r.persons || ""} onChange={(e) => update(i, { persons: e.target.value })} /></td>
                                <td className="p-1"><textarea rows={2} disabled={!canEdit} className={cell} style={{ borderColor: "#E5E0DA" }} value={r.consequences || ""} onChange={(e) => update(i, { consequences: e.target.value })} /></td>
                                <td className="p-1"><select disabled={!canEdit} data-testid={`tmp-risk-rating-${i}`} className="border rounded px-1.5 py-1 text-[13px]" style={{ borderColor: "#E5E0DA" }} value={r.risk_rating || ""} onChange={(e) => update(i, { risk_rating: e.target.value })}>{RATINGS.map((x) => <option key={x} value={x}>{x || "—"}</option>)}</select></td>
                                <td className="p-1"><textarea rows={2} disabled={!canEdit} className={cell} style={{ borderColor: "#E5E0DA" }} value={r.controls || ""} onChange={(e) => update(i, { controls: e.target.value })} /></td>
                                <td className="p-1"><select disabled={!canEdit} data-testid={`tmp-risk-residual-${i}`} className="border rounded px-1.5 py-1 text-[13px]" style={{ borderColor: "#E5E0DA" }} value={r.residual || ""} onChange={(e) => update(i, { residual: e.target.value })}>{RATINGS.map((x) => <option key={x} value={x}>{x || "—"}</option>)}</select></td>
                                <td className="p-1">{canEdit && <button onClick={() => patch({ risk_rows: rows.filter((_, ri) => ri !== i) })} className="text-[#B33A2E]"><Trash2 className="w-4 h-4" /></button>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {rows.length === 0 && <p className="text-sm text-[#6F6A64] mt-3">No hazards yet — add one to begin.</p>}
            </div>
        </div>
    );
}

function VersionsTab({ rec, reload, canEdit }) {
    const [label, setLabel] = useState("");
    const [saving, setSaving] = useState(false);
    const versions = rec.versions || [];
    const snapshot = async () => {
        setSaving(true);
        try { await api.post(`/tmp/${rec.id}/versions`, { label: label || null }); toast.success("Revision snapshot saved"); setLabel(""); await reload(); }
        catch (e) { toast.error("Snapshot failed"); } finally { setSaving(false); }
    };
    return (
        <div className="space-y-4 max-w-3xl">
            {canEdit && (
                <div className="bg-white border rounded-2xl p-4 flex items-end gap-3" style={{ borderColor: "#E5E0DA" }}>
                    <F label="Revision label (optional)">
                        <input className={inputCls} style={{ borderColor: "#E5E0DA" }} data-testid="tmp-version-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Issued for construction" />
                    </F>
                    <button onClick={snapshot} disabled={saving} data-testid="tmp-snapshot-button"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60 whitespace-nowrap" style={{ background: "#C75A1B" }}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <History className="w-4 h-4" />} Snapshot revision
                    </button>
                </div>
            )}
            <div className="bg-white border rounded-2xl p-4" style={{ borderColor: "#E5E0DA" }} data-testid="tmp-version-list">
                <h3 className="font-semibold mb-2">Revision history</h3>
                {versions.length === 0 ? <p className="text-sm text-[#6F6A64]">No revisions yet. Working version is v{rec.version}.</p> : (
                    <ul className="divide-y" style={{ borderColor: "#EDE9E3" }}>
                        {[...versions].reverse().map((v) => (
                            <li key={v.id} className="py-2.5">
                                <div className="text-sm font-semibold">v{v.version} {v.label && <span className="font-normal text-[#6F6A64]">· {v.label}</span>} {v.status && <Pill tone={STATUS_TONE[v.status]} className="ml-1">{v.status}</Pill>}</div>
                                <div className="text-xs text-[#9A958C]">{new Date(v.created_at).toLocaleString()} · {v.created_by_name || "—"}</div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
